import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { ConfigProvider } from 'antd';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Context
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Components
import PrivateRoute from './components/Auth/PrivateRoute';
import AdminRoute from './components/Auth/AdminRoute';
import SuperAdminRoute from './components/Auth/SuperAdminRoute';
import NewLayout from './components/Layout/NewLayout';
import ErrorBoundary from './components/Common/ErrorBoundary';

// Pages
import Dashboard from './pages/Dashboard';
import Login from './pages/Login';
import UsersPage from './pages/UsersPage';
import UserForm from './pages/users/UserForm';
import UserDetail from './pages/users/UserDetail';
import TechniciansPage from './pages/TechniciansPage';
import NotificationsPage from './pages/NotificationsPage';
import OrdersPage from './pages/OrdersPage';
import OrderDetail from './pages/orders/OrderDetail';
import CustomersPage from './pages/CustomersPage';
import CustomerDetail from './pages/CustomerDetail';
import CustomerForm from './pages/CustomerForm';
import ProductsPage from './pages/ProductsPage';
import ComplaintsPage from './pages/ComplaintsPage';
import ContactRequestsPage from './pages/ContactRequestsPage';
import ComplaintDetail from './pages/complaints/ComplaintDetail';
import ComplaintEdit from './pages/complaints/ComplaintEdit';
import ComplaintCreate from './pages/complaints/ComplaintCreate';
import NotFound from './pages/NotFound';
import EquipmentsPage from './pages/EquipmentsPage';
import MaintenanceSchedulePage from './pages/MaintenanceSchedulePage';
import InterventionsPage from './pages/InterventionsPage';
import ContractsPage from './pages/ContractsPage';
import ScheduledContractsPage from './pages/ScheduledContractsPage';
import ParametresPage from './pages/ParametresPage';
import QuotesPage from './pages/QuotesPage';
import QuoteDetail from './pages/quotes/QuoteDetail';
import QuoteForm from './pages/quotes/QuoteForm';
import DiagnosticReportsPage from './pages/DiagnosticReportsPage';
import CreateQuoteFromReportPage from './pages/CreateQuoteFromReportPage';
import PromotionsPage from './pages/PromotionsPage';
import InterventionReportsPage from './pages/InterventionReportsPage';
import MaintenanceOffersPage from './pages/MaintenanceOffersPage';
import SystemLogsPage from './pages/SystemLogsPage';
import DepannagePage from './pages/DepannagePage';
import ChatPage from './pages/ChatPage';
import BroadcastNotificationPage from './pages/BroadcastNotificationPage';
import AnalyticsPage from './pages/AnalyticsPage';
import SplitsPage from './pages/SplitsPage';
import SubscriptionPaymentPage from './pages/SubscriptionPaymentPage';
import ReferralPage from './pages/ReferralPage';
import QRCodesPage from './pages/QRCodesPage';
import MctCollaboratorsPage from './pages/MctCollaboratorsPage';

const theme = createTheme({
  palette: {
    primary: {
      main: '#0a543d',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

// Composant pour gérer les routes protégées
const AppRoutes = () => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Redirige vers le tableau de bord si l'utilisateur est déjà connecté
  useEffect(() => {
    if (isAuthenticated && location.pathname === '/login') {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, location.pathname, navigate]);

  // Routes publiques (accessibles sans authentification)
  const publicPaths = ['/login', '/payment'];
  const isPublicPath = publicPaths.some(path => location.pathname.startsWith(path));

  // Si l'utilisateur n'est pas authentifié et n'est pas sur une page publique, on le redirige
  if (!isAuthenticated && !isPublicPath) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return (
    <Routes>
      {/* Routes publiques */}
      <Route 
        path="/payment/subscription/:subscriptionId" 
        element={<SubscriptionPaymentPage />} 
      />
      
      <Route 
        path="/login" 
        element={!isAuthenticated ? <Login /> : <Navigate to="/dashboard" replace />} 
      />
      
      {/* Routes protégées */}
      <Route
        path="/dashboard"
        element={
          <PrivateRoute>
            <NewLayout>
              <Dashboard />
            </NewLayout>
          </PrivateRoute>
        }
      />
      
      <Route
        path="/utilisateurs"
        element={
          <PrivateRoute>
            <NewLayout>
              <UsersPage />
            </NewLayout>
          </PrivateRoute>
        }
      />
      
      <Route
        path="/techniciens"
        element={
          <PrivateRoute>
            <NewLayout>
              <TechniciansPage />
            </NewLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/equipements"
        element={
          <PrivateRoute>
            <NewLayout>
              <EquipmentsPage />
            </NewLayout>
          </PrivateRoute>
        }
      />

      <Route
        path="/splits"
        element={
          <SuperAdminRoute>
            <NewLayout>
              <SplitsPage />
            </NewLayout>
          </SuperAdminRoute>
        }
      />

      <Route
        path="/qr-codes"
        element={
          <SuperAdminRoute>
            <NewLayout>
              <QRCodesPage />
            </NewLayout>
          </SuperAdminRoute>
        }
      />

      <Route
        path="/campagnes-qr"
        element={
          <SuperAdminRoute>
            <NewLayout>
              <QRCodesPage />
            </NewLayout>
          </SuperAdminRoute>
        }
      />

      <Route
        path="/interventions"
        element={
          <PrivateRoute>
            <NewLayout>
              <InterventionsPage />
            </NewLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/interventions/:id"
        element={
          <PrivateRoute>
            <NewLayout>
              <InterventionsPage />
            </NewLayout>
          </PrivateRoute>
        }
      />

      <Route
        path="/depannage"
        element={
          <PrivateRoute>
            <NewLayout>
              <DepannagePage />
            </NewLayout>
          </PrivateRoute>
        }
      />

      <Route
        path="/contrats"
        element={
          <PrivateRoute>
            <NewLayout>
              <ContractsPage />
            </NewLayout>
          </PrivateRoute>
        }
      />

      <Route
        path="/contrats-programmes"
        element={
          <PrivateRoute>
            <NewLayout>
              <ScheduledContractsPage />
            </NewLayout>
          </PrivateRoute>
        }
      />

      <Route
        path="/parrainage"
        element={
          <PrivateRoute>
            <NewLayout>
              <ReferralPage />
            </NewLayout>
          </PrivateRoute>
        }
      />

      <Route
        path="/collaborateurs-mct"
        element={
          <SuperAdminRoute>
            <NewLayout>
              <MctCollaboratorsPage />
            </NewLayout>
          </SuperAdminRoute>
        }
      />

      <Route
        path="/parametres"
        element={
          <SuperAdminRoute>
            <NewLayout>
              <ParametresPage />
            </NewLayout>
          </SuperAdminRoute>
        }
      />

      <Route
        path="/planification"
        element={
          <PrivateRoute>
            <NewLayout>
              <MaintenanceSchedulePage />
            </NewLayout>
          </PrivateRoute>
        }
      />
      

      <Route
        path="/clients"
        element={
          <PrivateRoute>
            <NewLayout>
              <CustomersPage />
            </NewLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/customers/new"
        element={
          <PrivateRoute>
            <NewLayout>
              <CustomerForm />
            </NewLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/customers/:id"
        element={
          <PrivateRoute>
            <NewLayout>
              <CustomerDetail />
            </NewLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/customers/edit/:id"
        element={
          <PrivateRoute>
            <NewLayout>
              <CustomerForm />
            </NewLayout>
          </PrivateRoute>
        }
      />
      
      {/* Autres routes protégées */}
      <Route
        path="/diagnostic-reports"
        element={
          <PrivateRoute>
            <NewLayout>
              <DiagnosticReportsPage />
            </NewLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/quotes/create-from-report/:reportId"
        element={
          <PrivateRoute>
            <NewLayout>
              <CreateQuoteFromReportPage />
            </NewLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/notifications"
        element={
          <PrivateRoute>
            <NewLayout>
              <NotificationsPage />
            </NewLayout>
          </PrivateRoute>
        }
      />
      
      <Route
        path="/broadcast-notification"
        element={
          <AdminRoute>
            <NewLayout>
              <BroadcastNotificationPage />
            </NewLayout>
          </AdminRoute>
        }
      />
      
      <Route
        path="/chat"
        element={
          <PrivateRoute>
            <NewLayout>
              <ChatPage />
            </NewLayout>
          </PrivateRoute>
        }
      />
      
      <Route
        path="/rapports-interventions"
        element={
          <PrivateRoute>
            <NewLayout>
              <InterventionReportsPage />
            </NewLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/commandes"
        element={
          <PrivateRoute>
            <NewLayout>
              <OrdersPage />
            </NewLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/commandes/:id"
        element={
          <PrivateRoute>
            <NewLayout>
              <OrderDetail />
            </NewLayout>
          </PrivateRoute>
        }
      />
      
      <Route
        path="/produits"
        element={
          <AdminRoute>
            <NewLayout>
              <ProductsPage />
            </NewLayout>
          </AdminRoute>
        }
      />
      
      <Route
        path="/reclamations"
        element={
          <PrivateRoute>
            <NewLayout>
              <ComplaintsPage />
            </NewLayout>
          </PrivateRoute>
        }
      />
      
      <Route
        path="/demandes-rappel"
        element={
          <PrivateRoute>
            <NewLayout>
              <ContactRequestsPage />
            </NewLayout>
          </PrivateRoute>
        }
      />
      
      <Route
        path="/reclamations/nouvelle"
        element={
          <PrivateRoute>
            <NewLayout>
              <ComplaintCreate />
            </NewLayout>
          </PrivateRoute>
        }
      />
      
      <Route
        path="/reclamations/:id"
        element={
          <PrivateRoute>
            <NewLayout>
              <ComplaintDetail />
            </NewLayout>
          </PrivateRoute>
        }
      />
      
      <Route
        path="/reclamations/:id/modifier"
        element={
          <PrivateRoute>
            <NewLayout>
              <ComplaintEdit />
            </NewLayout>
          </PrivateRoute>
        }
      />
      
      <Route
        path="/promotions"
        element={
          <AdminRoute>
            <NewLayout>
              <PromotionsPage />
            </NewLayout>
          </AdminRoute>
        }
      />
      
      <Route
        path="/maintenance-offers"
        element={
          <PrivateRoute>
            <NewLayout>
              <MaintenanceOffersPage />
            </NewLayout>
          </PrivateRoute>
        }
      />
      
      {/* Routes utilisateurs */}
      <Route path="/users" element={
        <Navigate to="/utilisateurs" replace />
      } />
      <Route path="/users/new" element={
        <PrivateRoute>
          <NewLayout>
            <UserForm />
          </NewLayout>
        </PrivateRoute>
      } />
      <Route path="/users/edit/:id" element={
        <PrivateRoute>
          <NewLayout>
            <UserForm />
          </NewLayout>
        </PrivateRoute>
      } />
      <Route path="/users/:id" element={
        <PrivateRoute>
          <NewLayout>
            <UserDetail />
          </NewLayout>
        </PrivateRoute>
      } />
      
      {/* Routes pour les devis */}
      <Route
        path="/devis"
        element={
          <PrivateRoute>
            <NewLayout>
              <QuotesPage />
            </NewLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/devis/nouveau"
        element={
          <AdminRoute>
            <NewLayout>
              <QuoteForm />
            </NewLayout>
          </AdminRoute>
        }
      />
      <Route
        path="/devis/:id"
        element={
          <PrivateRoute>
            <NewLayout>
              <QuoteDetail />
            </NewLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/devis/:id/modifier"
        element={
          <AdminRoute>
            <NewLayout>
              <QuoteForm />
            </NewLayout>
          </AdminRoute>
        }
      />

      {/* Analytics Page */}
      <Route
        path="/analytics"
        element={
          <AdminRoute>
            <NewLayout>
              <AnalyticsPage />
            </NewLayout>
          </AdminRoute>
        }
      />

      {/* System Logs Page */}
      <Route
        path="/system-logs"
        element={
          <SuperAdminRoute>
            <NewLayout>
              <SystemLogsPage />
            </NewLayout>
          </SuperAdminRoute>
        }
      />
      
      {/* Redirection et page 404 */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="/not-found" element={<NotFound />} />
      <Route path="*" element={<Navigate to="/not-found" replace />} />
    </Routes>
  );
};
function App() {
  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: '#0a543d',
        },
      }}
    >
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
        <BrowserRouter>
          <AuthProvider>
            <ErrorBoundary>
              <AppRoutes />
            </ErrorBoundary>
          </AuthProvider>
        </BrowserRouter>
      </ThemeProvider>
    </ConfigProvider>
  );
}

export default App;